import React, { useState, useEffect, useRef } from "react";
import {
  Text,
  View,
  Platform,
  Pressable,
  ScrollView,
  Image,
  Dimensions,
  BackHandler,
} from "react-native";
import StyleSheet from "react-native-media-query";
import { StyleSheet as StyleSheetDef } from "react-native";
import { CHECK_ICON, DOWN_ARROW_ICON } from "../../resources/IMAGES";
import { useNavigation, useIsFocused } from "@react-navigation/native";
import { theme } from "../../theme/theme";
import { isTablet } from "react-native-device-info";
import { getFormattedAddressLabel } from "@lib/utilities/Formatters";
import { useAppDispatch, useAppSelector } from "@lib";
import { setLoading } from "@lib";
import { setCurrentPolicyNumber } from "@lib";

const DropDownView = (props: any) => {
  // state variables ::
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const scrollRef = useRef<ScrollView>(null);
  const dropdownRef = useRef<View>(null);
  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const { policyNumber } = useAppSelector((state) => state.policyReducer);
  const index = props?.arrayPolicyList?.findIndex((element: any) => {
    if (props?.policyNumber === element.policyNumber) {
      return true;
    }
  });

  // Close dropdown when screen loses focus or before navigation
  useEffect(() => {
    const unsubscribe = navigation.addListener("beforeRemove", () => {
      setIsDropdownVisible(false);
    });

    // Also close dropdown when screen loses focus
    if (!isFocused) {
      setIsDropdownVisible(false);
    }

    return unsubscribe;
  }, [navigation, isFocused]);

  // Handle hardware back button
  useEffect(() => {
    const backAction = () => {
      if (isDropdownVisible) {
        setIsDropdownVisible(false);
        return true;
      }
      return false;
    };

    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      backAction
    );

    return () => backHandler.remove();
  }, [isDropdownVisible]);

  // use effect ::
  useEffect(() => {
    setupUseEffect();
  });

  // Add this effect to notify parent component of dropdown state
  useEffect(() => {
    if (props.onDropdownVisibilityChange) {
      props.onDropdownVisibilityChange(isDropdownVisible);
    }
  }, [isDropdownVisible]);

  // setup function for use effect ::
  const setupUseEffect = () => {
    if (Platform.OS == "web") {
      setupDropDownViewForWeb();
    } else {
      setupDropDownViewForMobile();
    }
    handleClickOutsideForWeb();
  };

  // setup for web
  const setupDropDownViewForWeb = () => {
    setTimeout(() => {
      if (scrollRef.current) {
        let contentHeight = (scrollRef.current as any).scrollHeight;
        let length = props?.arrayPolicyList.length;
        let offset = index * (contentHeight / length);
        if (offset > 220) {
          // Max height of the dropdown
          (scrollRef.current as any).scrollTo({
            y: offset - contentHeight / length,
            animated: true,
          });
        }
      }
    }, 100);
  };

  // setup for mobile ::
  const setupDropDownViewForMobile = () => {
    let heightOfCell = 26;
    if (Dimensions.get("window").width <= 460) {
      heightOfCell = 45;
    }
    setTimeout(() => {
      if (scrollRef.current) {
        (scrollRef.current as any).scrollTo({
          y: index * heightOfCell,
          animated: true,
        });
      }
    }, 50);
  };

  // handle click outside for web
  const handleClickOutsideForWeb = () => {
    if (Platform.OS == "web") {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  };

  // action click outside for web
  function handleClickOutside(event: MouseEvent) {
    if (
      dropdownRef.current &&
      !(dropdownRef.current as any).contains(event.target)
    ) {
      setIsDropdownVisible(false);
    }
  }

  const getPolicyLabelText = (policyNumber: string) => {
    let policy = props?.arrayPolicyList?.find(
      (item: any) => item?.policyNumber === policyNumber
    );

    let policyStatus = policy?.policyStatus && `(${policy?.policyStatus})`;
    const productAdreess = getFormattedAddressLabel(
      policy?.physicalAddress ?? ""
    );
    return `${policy?.policyNumber} (${policy?.product}) | ${productAdreess} ${policyStatus}`;
  };

  const toggleDropdown = (value: boolean) => {
    setIsDropdownVisible(value);
  };

  const dispatch = useAppDispatch();
  return (
    <>
      <Pressable
        style={styles.dropdownMainContainer}
        onPress={() => {
          toggleDropdown(!isDropdownVisible);
        }}
        testID="policy-dropdown-select"
      >
        <View style={styles.bgTransparency} />
        <View
          style={[styles.pickerMobileSelectorView]}
          dataSet={{ media: ids.pickerMobileSelectorView }}
        >
          <Text
            numberOfLines={1}
            style={styles.listText}
            dataSet={{ media: ids.listText }}
            testID="policy-dropdown-label"
          >
            {getPolicyLabelText(policyNumber)}
          </Text>
        </View>
        <Image
          style={styles.arrowDown}
          dataSet={{ media: ids.arrowDown }}
          source={DOWN_ARROW_ICON}
        />
      </Pressable>
      {isDropdownVisible && (
        <>
          <View
            ref={dropdownRef as any}
            style={styles.dropdownContainer}
            dataSet={{ media: ids.dropdownContainer }}
          >
            <ScrollView
              bounces={false}
              nestedScrollEnabled={true}
              showsVerticalScrollIndicator={true}
              ref={scrollRef as any}
            >
              {props?.arrayPolicyList.map((policy: any, index:number) => {
                return (
                  <Pressable
                    onPress={() => {
                      dispatch(setLoading(true));
                      if (policyNumber == policy.policyNumber) {
                        dispatch(setLoading(false));
                      }
                      dispatch(setCurrentPolicyNumber(policy.policyNumber));
                      setIsDropdownVisible(false);
                    }}
                    key={policy?.policyNumber}
                    testID={`dropdown-item-select`}
                  >
                    <View
                      style={[styles.listChecked]}
                      dataSet={{ media: ids.inCentre }}
                    >
                      <View
                        style={styles.iconBlock}
                        dataSet={{ media: ids.iconBlock }}
                      >
                        {policy?.policyNumber === policyNumber && (
                          <Image
                            style={styles.checkIcon}
                            dataSet={{ media: ids.checkIcon }}
                            source={CHECK_ICON}
                          />
                        )}
                      </View>
                      <Text
                        style={[styles.WhiteText]}
                        dataSet={{ media: ids.WhiteText }}
                      >
                        {getPolicyLabelText(policy?.policyNumber)}{" "}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>
          {Platform.OS != "web" && (
            <Pressable
              style={styles.overlayTouch}
              dataSet={{ media: ids.overlayTouch }}
              onPress={() => {
                toggleDropdown(false);
              }}
            ></Pressable>
          )}
        </>
      )}
    </>
  );
};

const { ids, styles } = StyleSheet.create({
  inCentre: {
    display: "flex",
    justifyContent: "center",
  },
  pickerMobileSelectorView: {
    justifyContent: "center",
    height: 40,
    flex:1,
  },
  bgTransparency: {
    ...StyleSheetDef.absoluteFillObject,
    backgroundColor: "black",
    opacity: 0.302,
    borderRadius: 8,
  },
  dropdownMainContainer: {
    position: "relative",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    paddingHorizontal: 12,
  },
  arrowDown: {
    width: 10,
    height: 10,
    zIndex: theme.zIndex.max,
    tintColor: theme.colors.white,
  },
  overlayTouch: {
    position: "absolute",
    top: -90,
    bottom: 0,
    height: 2100,
    zIndex: 2,
    elevation: 2,
    ...Platform.select({
      ios: {
        "@media (min-width: 200px)": {
          right: -16,
          bottom: 0,
          left: -16,
          height: 800,
        },
        "@media (min-width: 700px)": {
          right: -400,
          bottom: 0,
          left: -400,
          minHeight: 800,
        },
      },
      android: {
        "@media (min-width: 200px)": {
          right: -16,
          bottom: 0,
          left: -16,
          height: 800,
        },
        "@media (min-width: 700px)": {
          right: -400,
          bottom: 0,
          left: -400,
          minHeight: 800,
        },
      },
      web: {
        "@media (min-width: 200px)": {
          right: -16,
          bottom: 0,
          left: -16,
          height: 800,
        },
        "@media (min-width: 1000px)": {
          right: -200,
          bottom: 0,
          left: -200,
          minHeight: 600,
        },
        "@media (min-width: 1400px)": {
          right: -200,
          bottom: 0,
          left: -200,
          minHeight: 800,
        },
      },
    }),
  },
  overlay: {
    backgroundColor: "green",
  },
  dropdownContainer: {
    overflowX: "hidden",
    maxHeight: 230,
    position: "absolute",
    top: 50,
    backgroundColor: theme.colors.gray,
    paddingBottom: 0,
    paddingRight: 12,
    borderRadius: 10,
    zIndex: 19,
    elevation: 9,
    // Shadow for iOS
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 8,
    // Shadow for web
    boxShadow: "0 4px 16px 0 rgba(0,0,0,0.18)",
    ...Platform.select({
      ios: {
        top: isTablet() ? 50 : 50,
        left: 0,
        right: 0,
      },
      android: {
        top: isTablet() ? 50 : 50,
        // Android shadow via elevation only
        "@media (min-width: 200px)": {
          left: 0,
          right: 0,
        },
        "@media (min-width: 700px)": {
          maxHeight: 170,
        },
      },
      web: {
        paddingRight: 10,
        top: 50,
        boxShadow: "0 4px 16px 0 rgba(0,0,0,0.18)",
        "@media (min-width: 200px)": {
          minWidth: "100%",
          position: "absolute",
          left: 0,
          right: 0,
        },
        "@media (min-width: 1000px)": {
          minWidth: 600,
        },
      },
    }),
  },
  WhiteText: {
    color: theme.colors.white,
    fontFamily: theme.fontFamily.robotoBold,
    ...Platform.select({
      web: {
        fontWeight: theme.fontweight.bold as any,
      },
    }),

    fontSize: theme.fontsize.cs,
    marginTop: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
    display: "flex",
    whiteSpace: "normal",
    flexWrap: "nowrap",
    maxWidth: "95%",
  },
  listText: {
    color: theme.colors.white,
    fontFamily: theme.fontFamily.robotoBold,
    ...Platform.select({
      web: {
        fontWeight: theme.fontweight.bold as any,
      },
    }),
    fontSize: theme.fontsize.cs,
  },
  listChecked: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    borderBottomColor: theme.colors.white + 10,
    borderBottomWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 6,
    minHeight: 30,
    "@media (min-width: 200px)": {
      left: 0,
      right: 0,
    },
    "@media (min-width: 700px)": {
      paddingVertical: 6,
    },
  },
  iconBlock: {
    display: "flex",
    justifyContent: "center",
    width: 22,
    height: 22,
    marginRight: 12,
  },
  checkIcon: {
    width: 18,
    height: 14,
    ...Platform.select({
      ios: {},
      android: {},
      web: {},
    }),
  },
});

export default DropDownView;
